import cloneDeep from 'lodash/cloneDeep';
import { useRef } from 'react';
import { message } from 'antd';
import { getFramework, getTargetType, useIdentity, useMoveEventListener } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { ITouchKitEvent } from '@/utils/event-kit';
import { ILayerProps, MAX_STAGE_WIDTH as MAX_STAGE_SIZE, MIN_STAGE_WIDTH as MIN_STAGE_SIZE } from '@/version-types';
import type { CropStagePlugin } from '../controller';
import { useStageCropStore } from '../models/crop-stage-state';
import { getOperateStageStyle, setScaleStageStyle } from '../utils';

const dataTypes = ['crop-rt', 'crop-lt', 'crop-rb', 'crop-lb'];

const adjustSize = (size: number) => {
    if (size < MIN_STAGE_SIZE) {
        return 50;
    } else if (size > MAX_STAGE_SIZE) {
        return MAX_STAGE_SIZE;
    }

    return size;
};

/**
 * 在图片编辑裁剪舞台挂载等比缩放手势，依赖当前画框、主图及裁剪操作状态
 *
 * 更新裁剪尺寸预览，并在手势结束时提交图层及同步视口。
 *
 * @param scale - 裁剪舞台的视口缩放比例
 */
export const useScaleOperate = (scale: number) => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const originIndex = framework.service.frame.getActiveFrameId();
    const getLayers = () => framework.service.frame.getLayers(originIndex);
    const getMainLayer = () => framework.service.frame.getMainLayer(originIndex)!;
    const cropController = framework.service.registration.getInstance<CropStagePlugin>('CropStagePlugin');

    const { $t } = getLocaleStore();

    const lastMainLayerRef = useRef<ILayerProps | null>(null);
    const lastLayersRef = useRef<ILayerProps[] | null>(null);
    const hasShownSizeLimitRef = useRef(false);
    const notifySizeLimit = () => {
        if (hasShownSizeLimitRef.current) return;
        hasShownSizeLimitRef.current = true;
        message.error($t((v) => v.stageManager.canvasLimitScope));
    };
    const { setIsCropStageOperating, setOperateBaseInfo } = useStageCropStore()((state) => state);
    const isStageAutoCrop = false;
    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target;
        const dataType = target.getAttribute('data-type');

        if (!getTargetType(target)) return;

        if (!dataTypes.includes(dataType || '')) return;

        if (e.isStart) {
            hasShownSizeLimitRef.current = false;
            const mainLayer = getMainLayer();
            const layers = getLayers();
            lastMainLayerRef.current = cloneDeep(mainLayer);
            lastLayersRef.current = cloneDeep(layers);
            cropController.emitPreviewStageSizeChange({ width: mainLayer.width, height: mainLayer.height });
            return;
        }

        if (!lastMainLayerRef.current || !lastLayersRef.current || !dataType) {
            return;
        }
        setIsCropStageOperating(true);

        const { left, top, width, height } = lastMainLayerRef.current;
        const params = {
            width,
            height,
            left,
            top,
        };

        const { displacementX, displacementY } = e;
        const moveDistance = {
            x: displacementX / scale,
            y: displacementY / scale,
        };

        // 截断前检查原始等比尺寸，避免宽度先被限制后无法触发提示
        const requestedWidth = width + (dataType === 'crop-lt' || dataType === 'crop-lb' ? -1 : 1) * moveDistance.x;
        const requestedHeight = (requestedWidth / width) * height;
        if (
            requestedWidth < MIN_STAGE_SIZE ||
            requestedWidth > MAX_STAGE_SIZE ||
            requestedHeight < MIN_STAGE_SIZE ||
            requestedHeight > MAX_STAGE_SIZE
        ) {
            notifySizeLimit();
        }

        if (dataType === 'crop-lt') {
            const val = params.width - moveDistance.x;

            params.width = adjustSize(val);
            params.left = params.left - moveDistance.x - (val - params.width);
            params.top = params.top - (params.height - (params.width / width) * height);
            params.height = (params.width / width) * height;

            if (params.height > MAX_STAGE_SIZE) {
                notifySizeLimit();
                params.top = params.top - (params.height - MAX_STAGE_SIZE);
                params.height = MAX_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                notifySizeLimit();
                params.top = params.top - (params.height - MIN_STAGE_SIZE);
                params.height = MIN_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }
        } else if (dataType === 'crop-rt') {
            const val = params.width + moveDistance.x;
            params.width = adjustSize(val);

            // 位置和高度必须使用受限后的宽度，避免控制器按位移反算出超限尺寸
            params.height = (params.width / width) * height;
            params.top = params.top + params.height - height;

            if (params.height > MAX_STAGE_SIZE) {
                notifySizeLimit();
                params.top = params.top - (params.height - MAX_STAGE_SIZE);
                params.height = MAX_STAGE_SIZE;
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                notifySizeLimit();
                params.top = params.top - (params.height - MIN_STAGE_SIZE);
                params.height = MIN_STAGE_SIZE;
                params.width = (params.height / height) * width;
            }
        } else if (dataType === 'crop-rb') {
            const val = params.width + moveDistance.x;
            params.width = adjustSize(val);

            params.height = (params.width / width) * height;

            if (params.height > MAX_STAGE_SIZE) {
                notifySizeLimit();
                params.height = MAX_STAGE_SIZE;
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                notifySizeLimit();
                params.height = MIN_STAGE_SIZE;
                params.width = (params.height / height) * width;
            }
        } else if (dataType === 'crop-lb') {
            const val = params.width - moveDistance.x;
            params.width = adjustSize(val);
            params.left = params.left - moveDistance.x - (val - params.width);

            params.height = (params.width / width) * height;

            if (params.height > MAX_STAGE_SIZE) {
                notifySizeLimit();
                params.height = MAX_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                notifySizeLimit();
                params.height = MIN_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }
        }

        const newLayerSize = cropController.moveScaleStageSizeOperate(
            params,
            cloneDeep(lastMainLayerRef.current),
            cloneDeep(lastLayersRef.current),
            dataType,
            isStageAutoCrop,
            e.isEnd,
        );

        if (newLayerSize) {
            if (!e.isEnd) {
                cropController.emitPreviewStageSizeChange({ width: newLayerSize.width, height: newLayerSize.height });
            }
            const nextScale = e.isEnd
                ? (cropController.syncCropFrameViewport(originIndex, newLayerSize) ?? scale)
                : scale;
            const originStageSize = e.isEnd ? newLayerSize : { width, height };
            const moveDirection = {
                x: e.displacementX ? e.displacementX / Math.abs(e.displacementX) : 1,
                y: e.displacementY ? e.displacementY / Math.abs(e.displacementY) : 1,
            };
            setScaleStageStyle(
                newLayerSize.width,
                newLayerSize.height,
                nextScale,
                dataType,
                isStageAutoCrop,
                moveDirection,
                originStageSize,
                e.isEnd,
            );
            const nextOperateBaseInfo = getOperateStageStyle(
                newLayerSize.width,
                newLayerSize.height,
                nextScale,
                dataType,
                isStageAutoCrop,
                moveDirection,
                originStageSize,
                e.isEnd,
            );
            if (nextOperateBaseInfo) {
                setOperateBaseInfo(nextOperateBaseInfo);
            }
        }

        if (e.isEnd) {
            lastMainLayerRef.current = null;
            lastLayersRef.current = null;
            cropController.emitPreviewStageSizeChange(null);
            setIsCropStageOperating(false);
        }
    });
};
